/** @format */

import { styled, keyframes } from "@mui/material/styles";
import { Typography, Box } from "@mui/material";
import { colors } from "../../utility/types/constant";

// ─── Keyframes ────────────────────────────────────────────────────────────────

const floatOrb = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%       { transform: translate(20px, -24px) scale(1.06); }
  66%       { transform: translate(-14px, 16px) scale(0.96); }
`;

const badgePulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(30, 193, 149, 0.55);
  }
  50% {
    transform: scale(1.04);
    box-shadow: 0 0 0 10px rgba(30, 193, 149, 0);
  }
`;

const shimmer = keyframes`
  0%   { background-position: -800px 0; }
  100% { background-position:  800px 0; }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const iconPop = keyframes`
  0%   { transform: scale(1) rotate(0deg); }
  40%  { transform: scale(1.18) rotate(-6deg); }
  70%  { transform: scale(0.95) rotate(3deg); }
  100% { transform: scale(1) rotate(0deg); }
`;

const lineGrow = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

// ─── Section Wrapper ──────────────────────────────────────────────────────────

export const SectionWrapper = styled("section")(({ theme }) => ({
  position: "relative",
  padding: "100px 24px 110px",
  background: "#ffffff",
  overflow: "hidden",

  // Subtle top/bottom gradient borders to separate from dark neighbours
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: `linear-gradient(90deg, transparent 0%, ${colors.mainSecondary} 40%, ${colors.primary} 100%)`,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.mainSecondary} 60%, transparent 100%)`,
  },

  [theme.breakpoints.down("md")]: { padding: "70px 16px 80px" },
  [theme.breakpoints.down("sm")]: { padding: "50px 12px 60px" },
}));

// ─── Background Decoration ────────────────────────────────────────────────────

export const BgOrb = styled("div")<{ size?: number }>(({ size = 300 }) => ({
  position: "absolute",
  width: size,
  height: size,
  borderRadius: "50%",
  pointerEvents: "none",
  animation: `${floatOrb} 18s ease-in-out infinite`,
  zIndex: 0,
}));

export const BgGrid = styled("div")({
  position: "absolute",
  inset: 0,
  backgroundImage: `
    linear-gradient(rgba(4,13,69,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(4,13,69,0.03) 1px, transparent 1px)
  `,
  backgroundSize: "52px 52px",
  zIndex: 0,
  pointerEvents: "none",
});

// ─── Header ───────────────────────────────────────────────────────────────────

export const HeaderBlock = styled("div")({
  textAlign: "center",
  marginBottom: "64px",
  position: "relative",
  zIndex: 2,
});

export const Badge = styled("div")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "9px 22px",
  borderRadius: "50px",
  background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`,
  color: "#fff",
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "0.78rem",
  fontWeight: 800,
  letterSpacing: "1.2px",
  textTransform: "uppercase",
  marginBottom: "22px",
  animation: `${badgePulse} 2.4s ease-in-out infinite`,
  boxShadow: "0 6px 22px rgba(30,193,149,0.32)",

  [theme.breakpoints.down("sm")]: {
    fontSize: "0.7rem",
    padding: "7px 16px",
  },
}));

export const MainHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "clamp(2rem, 4.5vw, 3.4rem)",
  fontWeight: 900,
  color: colors.primary,
  lineHeight: 1.15,
  letterSpacing: "-0.025em",
  marginBottom: "18px",

  [theme.breakpoints.down("sm")]: {
    fontSize: "clamp(1.75rem, 6vw, 2.4rem)",
  },
}));

export const Accent = styled("span")({
  background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  animation: `${shimmer} 3s ease-in-out infinite`,
  backgroundSize: "200% auto",
  display: "inline-block",
});

export const SubHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "clamp(0.95rem, 1.6vw, 1.15rem)",
  fontWeight: 400,
  color: "#64748b",
  maxWidth: "660px",
  margin: "0 auto",
  lineHeight: 1.85,

  [theme.breakpoints.down("sm")]: { fontSize: "0.95rem" },
}));

// ─── Two-Column Layout ────────────────────────────────────────────────────────

export const ContentGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "28px",
  position: "relative",
  zIndex: 2,

  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "16px",
  },
}));

// ─── Accordion Item ───────────────────────────────────────────────────────────

export const AccordionCard = styled("div")<{ isopen: string }>(
  ({ theme, isopen }) => ({
    background: isopen === "true" ? "#f0fdf8" : "#fafcff",
    borderRadius: "18px",
    border:
      isopen === "true"
        ? `1.5px solid ${colors.mainSecondary}`
        : "1.5px solid rgba(4,13,69,0.08)",
    boxShadow:
      isopen === "true"
        ? "0 10px 40px rgba(30,193,149,0.14)"
        : "0 2px 12px rgba(4,13,69,0.05)",
    overflow: "hidden",
    cursor: "pointer",
    transition:
      "border-color 0.3s ease, box-shadow 0.3s ease, background 0.3s ease, transform 0.25s ease",

    "&:hover": {
      borderColor: colors.mainSecondary,
      transform: "translateY(-2px)",
      boxShadow: "0 8px 28px rgba(30,193,149,0.18)",
    },

    [theme.breakpoints.down("sm")]: { borderRadius: "14px" },
  })
);

export const AccordionHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "16px",
  padding: "22px 24px",
  userSelect: "none",

  [theme.breakpoints.down("sm")]: { padding: "18px 16px", gap: "12px" },
}));

export const IconBox = styled("div")<{ isopen: string }>(
  ({ theme, isopen }) => ({
    flexShrink: 0,
    width: "52px",
    height: "52px",
    borderRadius: "14px",
    background:
      isopen === "true"
        ? `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`
        : `linear-gradient(135deg, rgba(4,13,69,0.07) 0%, rgba(4,13,69,0.04) 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.35s ease",
    boxShadow:
      isopen === "true" ? "0 4px 14px rgba(30,193,149,0.35)" : "none",

    "& svg": {
      fontSize: "24px",
      color: isopen === "true" ? "#fff" : colors.mainSecondary,
      transition: "color 0.3s ease",
      animation: isopen === "true" ? `${iconPop} 0.45s ease` : "none",
    },

    [theme.breakpoints.down("sm")]: {
      width: "44px",
      height: "44px",
      borderRadius: "12px",
      "& svg": { fontSize: "20px" },
    },
  })
);

export const QuestionText = styled(Typography)<{ isopen: string }>(
  ({ theme, isopen }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(0.95rem, 1.3vw, 1.1rem)",
    fontWeight: isopen === "true" ? 800 : 600,
    color: isopen === "true" ? colors.primary : "#374151",
    lineHeight: 1.4,
    flex: 1,
    transition: "color 0.3s ease, font-weight 0.2s ease",

    [theme.breakpoints.down("sm")]: { fontSize: "0.92rem" },
  })
);

export const ChevronBox = styled("div")<{ isopen: string }>(({ isopen }) => ({
  flexShrink: 0,
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  background:
    isopen === "true"
      ? `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`
      : "rgba(4,13,69,0.07)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background 0.3s ease, transform 0.35s ease",
  transform: isopen === "true" ? "rotate(45deg)" : "rotate(0deg)",

  "& svg": {
    fontSize: "18px",
    color: isopen === "true" ? "#fff" : "#94a3b8",
    transition: "color 0.3s ease",
  },
}));

// ─── Accordion Body (animated open/close) ─────────────────────────────────────

export const AccordionBody = styled("div")(({ theme }) => ({
  padding: "0 24px 24px 92px",
  animation: `${slideDown} 0.28s ease`,

  [theme.breakpoints.down("sm")]: {
    padding: "0 16px 20px 72px",
  },
}));

export const Divider = styled("div")({
  height: "1px",
  background: `linear-gradient(90deg, ${colors.mainSecondary}55, transparent)`,
  marginBottom: "18px",
  transformOrigin: "left",
  animation: `${lineGrow} 0.35s ease`,
});

export const AnswerText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "clamp(0.88rem, 1.1vw, 0.97rem)",
  fontWeight: 400,
  color: "#4b5563",
  lineHeight: 1.85,

  [theme.breakpoints.down("sm")]: { fontSize: "0.88rem" },
}));

export const TrustTag = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  marginTop: "12px",
  padding: "5px 12px",
  borderRadius: "20px",
  background: "rgba(30,193,149,0.1)",
  border: `1px solid rgba(30,193,149,0.3)`,
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "0.74rem",
  fontWeight: 700,
  color: colors.mainSecondary,
  letterSpacing: "0.4px",
  textTransform: "uppercase",
}));

// ─── Bottom CTA Strip ─────────────────────────────────────────────────────────

export const CTAStrip = styled("div")(({ theme }) => ({
  marginTop: "64px",
  padding: "40px 48px",
  borderRadius: "24px",
  background: `linear-gradient(135deg, ${colors.primary} 0%, #051442 50%, ${colors.thirdPrimary} 100%)`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "24px",
  position: "relative",
  zIndex: 2,
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(circle at 20% 60%, rgba(30,193,149,0.18) 0%, transparent 55%), radial-gradient(circle at 80% 30%, rgba(30,193,149,0.12) 0%, transparent 55%)",
    pointerEvents: "none",
  },

  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    textAlign: "center",
    padding: "32px 24px",
  },
  [theme.breakpoints.down("sm")]: { borderRadius: "16px", padding: "28px 16px" },
}));

export const CTALeft = styled("div")({
  position: "relative",
  zIndex: 1,
  flex: 1,
});

export const CTATitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "clamp(1.3rem, 2.2vw, 1.75rem)",
  fontWeight: 800,
  color: "#fff",
  marginBottom: "8px",
  letterSpacing: "-0.01em",
}));

export const CTASub = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "clamp(0.88rem, 1.2vw, 1rem)",
  fontWeight: 400,
  color: "rgba(255,255,255,0.75)",
  lineHeight: 1.7,
}));

export const CTAButtons = styled("div")(({ theme }) => ({
  display: "flex",
  gap: "12px",
  flexShrink: 0,
  flexWrap: "wrap",
  position: "relative",
  zIndex: 1,

  [theme.breakpoints.down("md")]: { justifyContent: "center" },
}));

export const PrimaryBtn = styled("a")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  padding: "13px 28px",
  borderRadius: "12px",
  textDecoration: "none",
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 800,
  fontSize: "0.9rem",
  color: colors.primary,
  background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`,
  boxShadow: "0 8px 24px rgba(30,193,149,0.3)",
  transition: "transform 0.22s ease, box-shadow 0.22s ease",

  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 30px rgba(30,193,149,0.42)",
  },
}));

export const SecondaryBtn = styled("a")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  padding: "13px 28px",
  borderRadius: "12px",
  textDecoration: "none",
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 700,
  fontSize: "0.9rem",
  color: "#fff",
  background: "rgba(255,255,255,0.08)",
  border: "1.5px solid rgba(255,255,255,0.2)",
  backdropFilter: "blur(8px)",
  transition: "all 0.22s ease",

  "&:hover": {
    background: "rgba(255,255,255,0.13)",
    borderColor: `${colors.mainSecondary}88`,
    transform: "translateY(-2px)",
  },
}));
